import api from 'utils/api'
import { beginLogout, completeLogout, getSession, isCurrentSession, markSessionValidated } from 'utils/session'
import { getBrowserPosition, getSavedLocation, persistLocation, getUseBrowserLocation, getSFDefaultLocation } from 'utils/location'
import { getParameterByName } from 'utils/functions'
import * as types from './actionTypes'

const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

export const fetchUser = () => dispatch => {
  const context = getSession()
  return api.get('/user/me')
    .then(res => {
      if (!isCurrentSession(context)) return
      markSessionValidated(context)
      dispatch(fetchUserSuccess(res))
      return res
    })
    .catch(error => {
      if (isCurrentSession(context) && !error.sessionHandled) dispatch(fetchUserError())
    })
}

export const fetchUserSuccess = (userData) => {
  return {
    type: types.FETCH_USER_SUCCESS,
    details: userData,
  }
}

export const fetchUserError = () => ({
  type: types.FETCH_USER_ERROR,
})

export const logoutUser = () => async () => {
  const context = getSession()
  if (context.ending) return
  beginLogout()
  const controller = new AbortController()
  let timeout
  try {
    await Promise.race([
      api.get('/auth/logout', { signal: controller.signal }),
      new Promise(resolve => {
        timeout = setTimeout(() => { controller.abort(); resolve() }, 5000)
      }),
    ])
  } catch (_) {
    // Local logout must complete even if the server is unavailable or the token expired.
  } finally {
    clearTimeout(timeout)
    completeLogout(context)
  }
}

export const logout = () => ({
  type: types.LOGOUT,
})

const fetchLocation = (location) => ({
  type: types.FETCH_LOCATION,
  location,
})

export const fetchUserLocation = () => async (dispatch) => {
  const useBrowserLocation = getUseBrowserLocation()

  // If user has selected "Use my current position", always check current browser location first
  // (ignore saved location to always get fresh location)
  if (useBrowserLocation) {
    // Try to get browser location first if permission is granted
    if (navigator.permissions) {
      try {
        const geoPermission = await navigator.permissions.query({ name: 'geolocation' })
        if (geoPermission.state === 'granted') {
          try {
            const { latitude, longitude } = await getBrowserPosition()
            const fullLocation = await api.post('/geo', { latitude, longitude })
            dispatch(changeLocation(fullLocation))
            return
          } catch (error) {
            // If precise location fails, fall through to IP-based location
            console.warn('Failed to get precise location:', error)
          }
        }
      } catch (error) {
        // Permissions API not supported or failed, continue with IP-based location
        console.warn('Could not check geolocation permission:', error)
      }
    }

    // If browser location is not available, try to get it directly (for browsers without Permissions API)
    if (navigator.geolocation) {
      try {
        const { latitude, longitude } = await getBrowserPosition()
        const fullLocation = await api.post('/geo', { latitude, longitude })
        dispatch(changeLocation(fullLocation))
        return
      } catch (error) {
        // If browser location fails, fall through to IP-based location
        console.warn('Failed to get browser location:', error)
      }
    }
  }

  // If preference is not set, check for saved location
  const savedLocation = getSavedLocation()
  if (savedLocation) {
    const isLoggedOut = getParameterByName('logout')
    if (isLoggedOut) {
      // On logout, refresh location from backend
      const { latitude, longitude } = savedLocation
      try {
        const fullLocation = await api.post('/geo', { latitude, longitude })
        dispatch(changeLocation(fullLocation))
      } catch (error) {
        console.error("Failed to refresh location:", error.message)
        // Fall back to saved location
        dispatch(fetchLocation(savedLocation))
      }
      return
    }
    // Use saved location
    dispatch(fetchLocation(savedLocation))
    return
  }

  // If permission is already granted (but preference not set), use precise location
  // (LocationPermissionDialog handles initial setup, this is for subsequent loads)
  if (navigator.permissions) {
    try {
      const geoPermission = await navigator.permissions.query({ name: 'geolocation' })
      if (geoPermission.state === 'granted') {
        try {
          const { latitude, longitude } = await getBrowserPosition()
          const fullLocation = await api.post('/geo', { latitude, longitude })
          dispatch(changeLocation(fullLocation))
          return
        } catch (error) {
          // If precise location fails, fall through to IP-based location
          console.warn('Failed to get precise location:', error)
        }
      }
    } catch (error) {
      // Permissions API not supported or failed, continue with IP-based location
      console.warn('Could not check geolocation permission:', error)
    }
  }

  // Fallback to IP-based location (IP Stack)
  // Only used if browser location preference is not set, or if browser location fails
  try {
    console.info('Fetching location from IP Stack (fallback)...')
    const res = await api.post('/geo', {})
    if (res && (res.latitude || res.longitude || res.city)) {
      console.info('IP Stack location response:', res)
      dispatch(changeLocation(res))
      return
    } else {
      console.error("Invalid IP Stack response:", res)
    }
  } catch (error) {
    console.error("Failed to fetch location from IP Stack:", error.message)
  }

  // If all methods failed (IPstack failed, no session, and permission check failed/not available)
  // Use SF default location as final fallback
  console.info('Using SF default location as final fallback')
  dispatch(changeLocation(getSFDefaultLocation()))
}

const normalizedLocation = location => {
  if (location?.latitude == null || location?.longitude == null || location.latitude === '' || location.longitude === '') return null
  const latitude = Number(location.latitude)
  const longitude = Number(location.longitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null
  return [latitude.toFixed(5), longitude.toFixed(5)]
}

export const hasMeaningfulLocationChange = (previous, current) => {
  const previousCoordinates = normalizedLocation(previous)
  const currentCoordinates = normalizedLocation(current)
  return Boolean(previousCoordinates && currentCoordinates && (
    previousCoordinates[0] !== currentCoordinates[0] || previousCoordinates[1] !== currentCoordinates[1]
  ))
}

const locationRequestKey = () => window.crypto?.randomUUID?.() || `location-${Date.now()}-${Math.random().toString(36).slice(2)}`

export const changeLocation = (location, { track = false } = {}) => (dispatch, getState) => {
  const previous = getState?.()?.user?.location
  dispatch(fetchLocation(location))
  persistLocation(location)
  if (!track || !hasMeaningfulLocationChange(previous, location)) return

  api.post('/common/homepage-location-changes', {
    previousLatitude: Number(previous.latitude),
    previousLongitude: Number(previous.longitude),
    latitude: Number(location.latitude),
    longitude: Number(location.longitude),
  }, { headers: { 'Idempotency-Key': locationRequestKey() } }).catch(() => {
    // Location selection remains successful when best-effort telemetry is unavailable.
  })
}

const fetchFavorites = (favorites) => ({
  type: types.FETCH_FAVORITES,
  favorites,
})

export const updateFavorite = (offerId, isFavorite) => ({
  type: types.UPDATE_FAVORITE,
  offerId,
  isFavorite,
})

export const fetchUserFavorites = () => (dispatch) => {
  const context = getSession()
  return api.get(`/favorite/ids`)
    .then((res) => {
      if (isCurrentSession(context)) dispatch(fetchFavorites(res))
    })
}

export const fetchBusinesses = (businesses) => ({
  type: types.FETCH_BUSINESSES,
  data: businesses,
})

export const cleanupBusinesses = (businesses) => ({
  type: types.CLEANUP_BUSINESSES,
  data: businesses,
})
